// Central SEO helper -- one place that owns everything inside <head> that
// search engines and social crawlers read: title, meta description, canonical,
// Open Graph/Twitter cards, robots directives and JSON-LD structured data.
//
// Deliberately imperative DOM manipulation rather than a library like
// react-helmet-async: this app already manipulates the head directly (the
// theme/dir effects in App.js, the original PDP meta effect), it avoids adding
// a dependency with its own React 19 peer-range caveats, and it keeps the
// whole surface in a single readable file.
//
// The one rule that makes this safe: applySeo is *idempotent and total*. Every
// call rewrites the full set of managed tags, and callers never "clean up" on
// unmount. An earlier design restored the previous title on unmount, which
// races badly -- a page that sets SEO once its data loads would get its title
// reverted by the outgoing page's cleanup. Instead, every route sets its own
// SEO on arrival and simply overwrites whatever the last route left behind.
//
// A note on languages: the app supports 4 UI languages, but the chosen
// language lives in localStorage rather than in the URL, so every URL has
// exactly one indexable version. Metadata is therefore written in English --
// the canonical version crawlers will index -- instead of following the
// viewer's UI language, which would make the indexed copy depend on whichever
// crawler locale happened to hit the page first.

const DEFAULT_SITE_URL = 'https://acymart.com';

// Absolute origin used for canonical/og:url/image URLs. Structured data and
// Open Graph both require absolute URLs -- relative ones are silently dropped
// by most crawlers. REACT_APP_SITE_URL should match the backend's SITE_URL;
// falling back to the live origin keeps preview/staging deploys self-consistent
// instead of pointing every canonical at production.
export const SITE_URL = (
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== 'undefined' && window.location?.origin) ||
  DEFAULT_SITE_URL
).replace(/\/$/, '');

export const SITE_NAME = 'AcyMart';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const DEFAULT_DESCRIPTION =
  'AcyMart — Discover More. Shop Smarter. A modern multi-vendor marketplace for electronics, fashion, home goods and more.';

// Turns any of the shapes product/store images come in into an absolute URL.
// Product images are stored as either bare strings or { url, view } objects
// (see the gallery builder in ProductDetailPage), and uploaded images are
// site-relative paths like /uploads/xyz.jpg.
export function absoluteUrl(value) {
  const raw = typeof value === 'string' ? value : value?.url;
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('data:')) return null; // never a valid og:image/JSON-LD target
  return `${SITE_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

// Meta descriptions get truncated by search engines around 155-160 chars;
// trimming on a word boundary avoids a mid-word cut in the SERP snippet.
export function truncate(text, max = 155) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, clean.lastIndexOf(' ', max - 1) || max - 1).trim()}…`;
}

// Every tag this module owns carries data-seo, so a single query finds the
// full managed set without touching the static tags in index.html that it
// doesn't control.
const MANAGED = 'data-seo';

function upsertMeta(selector, attr, name, content) {
  let el = document.head.querySelector(selector);
  if (!content) {
    // Only ever remove a tag this module created -- index.html's own defaults
    // are the fallback and must survive a page that omits the field.
    if (el?.hasAttribute(MANAGED)) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    // Removed unconditionally, unlike upsertMeta's managed-only rule. That
    // rule exists because index.html's meta defaults are a reasonable
    // fallback for a page that doesn't set its own -- but there is no such
    // thing as a default canonical. index.html ships one pointing at the
    // homepage (and the backend injects one on product/store URLs); leaving
    // either in place on a page that wants no canonical would have every
    // noindex page declaring the homepage as its canonical URL.
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Structured-data blocks are keyed by id so a page can replace its own graph
// without disturbing the site-wide Organization/WebSite blocks that ship
// statically in index.html.
function upsertJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  const el = existing || document.createElement('script');
  if (!existing) {
    el.type = 'application/ld+json';
    el.id = id;
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  // Drop undefined values rather than letting JSON.stringify emit keys that
  // Google's validator flags as malformed.
  el.textContent = JSON.stringify(data, (_k, v) => (v === undefined ? undefined : v));
}

/**
 * Apply the full SEO head state for the current page.
 *
 * @param {object}  o
 * @param {string}  o.title        Page title (site name is appended unless it already contains it).
 * @param {string}  o.description  Meta description; truncated to a SERP-safe length.
 * @param {string}  o.path         Site-relative path used for canonical + og:url. Query strings
 *                                 are intentionally excluded by callers so filtered/sorted views
 *                                 consolidate onto one canonical URL.
 * @param {string}  o.image        Absolute or site-relative preview image.
 * @param {string}  o.type         Open Graph type ('website' | 'product' | 'article').
 * @param {boolean} o.noindex      Keep the page out of search results (private/thin pages).
 * @param {object}  o.jsonLd       Structured data graph for this page.
 */
export function applySeo({
  title,
  description,
  path = typeof window !== 'undefined' ? window.location.pathname : '/',
  image,
  type = 'website',
  noindex = false,
  jsonLd = null,
} = {}) {
  if (typeof document === 'undefined') return;

  const fullTitle = !title
    ? SITE_NAME
    : title.includes(SITE_NAME)
      ? title
      : `${title} | ${SITE_NAME}`;
  const desc = truncate(description || DEFAULT_DESCRIPTION);
  const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const img = absoluteUrl(image) || DEFAULT_IMAGE;

  document.title = fullTitle;
  upsertMeta('meta[name="description"]', 'name', 'description', desc);

  // A canonical is pointless on a page that is also telling crawlers not to
  // index it, and the pair is a common source of Search Console warnings.
  upsertLink('canonical', noindex ? null : canonical);
  upsertMeta(
    'meta[name="robots"]',
    'name',
    'robots',
    noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'
  );

  upsertMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
  upsertMeta('meta[property="og:description"]', 'property', 'og:description', desc);
  upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
  upsertMeta('meta[property="og:type"]', 'property', 'og:type', type);
  upsertMeta('meta[property="og:image"]', 'property', 'og:image', img);
  upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);

  upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
  upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);
  upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', desc);
  upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', img);

  upsertJsonLd('seo-ld-page', jsonLd);
}

// Breadcrumb structured data drives the "Home › Electronics › Product" trail
// that replaces the raw URL in Google results.
export function breadcrumbLd(trail) {
  const items = trail.filter((c) => c && c.name);
  if (items.length < 2) return undefined;
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: crumb.path ? `${SITE_URL}${crumb.path}` : undefined,
    })),
  };
}

// ── Per-route metadata ─────────────────────────────────────────────────────
// Keyed by the same `page` identifiers App.js derives from the pathname, so
// adding a route to that switch and to this map is the whole job.
//
// `noindex: true` marks pages that must never reach a search index: anything
// behind auth (an account page crawled while logged out is an empty shell),
// transactional funnels, and one-off tokenised links. robots.txt blocks the
// admin/seller portals outright; these directives cover the rest, including
// URLs a crawler may reach from an external link.
export const PAGE_SEO = {
  home: {
    title: 'AcyMart - Online Marketplace | Discover More. Shop Smarter.',
    description:
      'Shop electronics, fashion, home goods, beauty and more from trusted sellers on AcyMart. Fast delivery, secure payment and easy returns.',
  },
  deals: {
    title: "Today's Deals & Discounts",
    description:
      'Browse the latest discounts and limited-time offers on AcyMart. New deals added daily across electronics, fashion, home and beauty.',
  },
  brands: {
    title: 'Shop by Brand',
    description: 'Explore all the brands available on AcyMart and shop their products in one place.',
  },
  about: {
    title: 'About Us',
    description:
      'Learn about AcyMart, the multi-vendor marketplace connecting shoppers with trusted local and international sellers.',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with the AcyMart team. We usually reply within one business day.',
  },
  careers: {
    title: 'Careers',
    description: 'Join the AcyMart team. Browse open roles and apply online.',
  },
  faq: {
    title: 'Frequently Asked Questions',
    description: 'Answers to common questions about ordering, delivery, payment and returns on AcyMart.',
  },
  helpCenter: {
    title: 'Help Center',
    description: 'Guides and support articles for shopping, orders, returns and your AcyMart account.',
  },
  sellerFaq: {
    title: 'Seller FAQs',
    description: 'Answers to common questions from sellers about listing products, payouts and store management on AcyMart.',
  },
  'seller-registration': {
    title: 'Become a Seller',
    description:
      'Start selling on AcyMart. Reach more customers, manage your store online and get paid securely. Register your store today.',
  },
  'commission-plan': {
    title: 'Seller Commission Plans',
    description: 'Commission rates and seller tiers on AcyMart, explained in full.',
  },
  'seller-guidelines': {
    title: 'Seller Guidelines',
    description: 'The rules, quality standards and listing policies every AcyMart seller agrees to.',
  },
  'news-updates': {
    title: 'News & Updates',
    description: 'Product announcements, marketplace news and platform updates from AcyMart.',
  },
  // Needs an entry even though the page is plain and static: the effect in
  // App.js marks any page MISSING from this map noindex, and /app is listed in
  // the sitemap -- a noindex page in the sitemap is exactly the contradiction
  // the SEO notes in CLAUDE.md warn about.
  'get-app': {
    title: 'Get the AcyMart App',
    description: 'Install AcyMart on your phone. Download the Android app or add the web app to your home screen for faster browsing and offline access.',
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'How AcyMart collects, uses and protects your personal data.',
  },
  terms: {
    title: 'Terms & Conditions',
    description: 'The terms governing your use of the AcyMart marketplace.',
  },
  cookies: {
    title: 'Cookie Policy',
    description: 'How AcyMart uses cookies and similar technologies, and how to control them.',
  },
  returns: {
    title: 'Returns & Refunds Policy',
    description: 'How to return an item and how refunds are processed on AcyMart.',
  },
  shipping: {
    title: 'Shipping Policy',
    description: 'Delivery options, shipping times and costs for AcyMart orders.',
  },

  // Auth-gated, transactional or tokenised -- valuable to users, worthless
  // (or actively harmful) in a search index.
  cart: { title: 'Your Cart', noindex: true },
  orders: { title: 'Your Orders', noindex: true },
  dispute: { title: 'Disputes', noindex: true },
  wishlist: { title: 'Your Wishlist', noindex: true },
  address: { title: 'Address Book', noindex: true },
  settings: { title: 'Account Settings', noindex: true },
  profile: { title: 'Your Profile', noindex: true },
  dashboard: { title: 'Your Dashboard', noindex: true },
  notifications: { title: 'Notifications', noindex: true },
  'track-order': { title: 'Track Your Order', noindex: true },
  'track-application': { title: 'Track Your Seller Application', noindex: true },
  'delivery-confirm': { title: 'Confirm Delivery', noindex: true },
  'shared-wishlist': { title: 'Shared Wishlist', noindex: true },
  // Reached only from a cart-recovery email. Tokenised and private, so never
  // indexed; without an entry here the page was titled "Page Not Found".
  'cart-recover': { title: 'Your Saved Cart', noindex: true },
  'cart-reminders-off': { title: 'Email Preferences', noindex: true },
  'seller-portal': { title: 'Seller Portal', noindex: true },
  'admin-login': { title: 'Portal Access', noindex: true },
  admin: { title: 'Admin Dashboard', noindex: true },
  'super-admin': { title: 'Super Admin', noindex: true },
};

// Category pages are generated rather than listed one by one -- the category
// name is the only thing that varies.
export function categorySeo(category) {
  const name = String(category || '').trim();
  return {
    title: `${name} — Shop ${name} Online`,
    description: `Shop ${name} on AcyMart. Compare prices from trusted sellers, with fast delivery, secure payment and easy returns.`,
  };
}
